import useTranslator from "../../hooks/useTranslator";
import Cancelimg from "../../assets/img/cancel-01.svg";
import { Form } from "react-bootstrap";
import { useMemo, useState } from "react";
import { isValidUrl, streetTypes } from "../../helper/common";
import Select from "react-select";
import provincesData from "../../Utils/provincia_municipios.json";

const FullAddressFormModal = ({
  isLandlord,
  setIsAddManuallyModal,
  formData,
  handleInputChange,
  handleSubmit,
  setStep,
  onClose,
  isEdit = false,
  submitLoading,
}) => {
  const { _t } = useTranslator();
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const requiredFields = useMemo(() => {
    const fields = [
      "street",
      "street_number",
      "postcode",
      "floor",
      "door",
      "province",
      "municipality",
    ];
    if (!isEdit) fields.unshift("street_type");
    return fields;
  }, [isEdit]);

  const fieldErrorKeyMap = {
    street_type: "streetType",
    street: "street",
    street_number: "number",
    postcode: "postCode",
    floor: "floorNumber",
    door: "letter",
    province: "province",
    municipality: "municipality",
  };

  const getFieldErrorMessage = (field) => {
    const label = _t(fieldErrorKeyMap[field]);
    const translatedRequiredSuffix = _t("isRequired");
    const requiredSuffix =
      translatedRequiredSuffix === "isRequired"
        ? "is required"
        : translatedRequiredSuffix;
    return `${label} ${requiredSuffix}`;
  };

  const validateForm = () => {
    const errors = {};
    requiredFields.forEach((field) => {
      if (!String(formData?.[field] ?? "").trim()) {
        errors[field] = true;
      }
    });
    const adUrl = String(formData?.ad_url ?? "").trim();
    if (adUrl && !isValidUrl(adUrl)) {
      errors.ad_url = true;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    setHasSubmitted(true);
    if (!validateForm()) return;
    handleSubmit(e);
  };

  const handleFieldChange = (field, value, event) => {
    if (hasSubmitted) {
      const isEmpty = !String(value ?? "").trim();
      setFieldErrors((prev) => ({ ...prev, [field]: isEmpty }));
    }

    if (event) {
      handleInputChange(event);
    } else {
      handleInputChange({
        target: {
          name: field,
          value,
        },
      });
    }
  };

  const handleAdUrlChange = (event) => {
    const { value } = event.target;
    handleInputChange(event);

    if (hasSubmitted) {
      setFieldErrors((prev) => ({
        ...prev,
        ad_url: Boolean(value.trim()) && !isValidUrl(value),
      }));
    }
  };

  const compactSelectStyles = (custom = {}) => ({
    control: (base) => ({
      ...base,
      minHeight: "none",
      height: "var(--select-height, 20px)",
      border: "none",
      boxShadow: "none",
      fontSize: "var(--select-font-size, 0.972vw)",
      paddingLeft: "0",
      cursor: "pointer",
      backgroundColor: "transparent",
      display: "flex",
      alignItems: "center",
      ...custom.control,
    }),
    valueContainer: (base, state) => ({
      ...base,
      height: "var(--select-height, 20px)",
      padding: "0",
      display: "flex",
      alignItems: "center",
      overflow: "visible",
      ...(state.isDisabled && {
        paddingTop: "1px",
      }),
      ...custom.valueContainer,
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      textAlign: "left",
      overflow: "visible",
      color: "#000",
      ...custom.singleValue,
    }),
    placeholder: (base) => ({
      ...base,
      textAlign: "left",
      margin: 0,
      overflow: "visible",
      color: "#7f7f7f",
      ...custom.placeholder,
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      height: "var(--select-height, 20px)",
      lineHeight: "var(--select-height, 20px)",
    }),
    indicatorsContainer: () => ({
      display: "none",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0",
      ...custom.dropdownIndicator,
    }),
  });

  return (
    <div
      className={`addressModal steps-wrapper ${isLandlord && "resend-text"}`}
    >
      <h4 className="step-name">
        {isEdit ? _t("editYourAddress") : _t("whatIsYourAddress")}
      </h4>
      <div
        className="cursor-pointer"
        onClick={() => setIsAddManuallyModal(false)}
      >
        <img src={Cancelimg} alt="icon" className="canceicon topRightIcon" />
      </div>
      <Form className="loginform" onSubmit={onFormSubmit} noValidate>
        <div className="auth-form authForm">
          {!isEdit && (
            <div
              className={`forminput form-mobile-gap customInput row100 relative ${fieldErrors.street_type ? "field-error" : ""}`}
              id="custom-select-search"
            >
              <label className="forminput label-text">
                {_t("streetType")} <span className="text-danger">*</span>
              </label>
              <Select
                className={`react-select-wrapper ${fieldErrors.street_type ? "is-invalid" : ""}`}
                classNamePrefix="react-select custom-react-select-styling"
                name="street_type"
                value={
                  formData?.street_type
                    ? {
                        label: formData.street_type,
                        value: formData.street_type,
                      }
                    : null
                }
                options={streetTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                placeholder={_t("enterStreetType")}
                onChange={(selected) =>
                  handleFieldChange("street_type", selected?.value || "")
                }
                styles={compactSelectStyles()}
                isSearchable
              />
              <img
                src="/images/down-arrow-grey.svg"
                className="downArrow"
                alt="arrow"
              />
              {fieldErrors.street_type && (
                <p className="field-error-message">
                  {getFieldErrorMessage("street_type")}
                </p>
              )}
            </div>
          )}
          <div
            className={`forminput form-mobile-padding customInput row50 ${fieldErrors.street ? "field-error" : ""}`}
          >
            <label className="forminput label-text">
              {_t("street")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control user-auth ${fieldErrors.street ? "is-invalid" : ""}`}
              name="street"
              value={formData?.street}
              onChange={(e) => handleFieldChange("street", e.target.value, e)}
              placeholder={_t("enterStreet")}
            />
            {fieldErrors.street && (
              <p className="field-error-message">
                {getFieldErrorMessage("street")}
              </p>
            )}
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 ${fieldErrors.street_number ? "field-error" : ""}`}
          >
            <label className="forminput label-text">
              {_t("number")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control user-auth ${fieldErrors.street_number ? "is-invalid" : ""}`}
              name="street_number"
              value={formData?.street_number}
              onChange={(e) =>
                handleFieldChange("street_number", e.target.value, e)
              }
              placeholder={_t("enterNumber")}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {fieldErrors.street_number && (
              <p className="field-error-message">
                {getFieldErrorMessage("street_number")}
              </p>
            )}
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 ${fieldErrors.postcode ? "field-error" : ""}`}
          >
            <label className="forminput label-text">
              {_t("postCode")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control user-auth ${fieldErrors.postcode ? "is-invalid" : ""}`}
              name="postcode"
              value={formData?.postcode}
              onChange={(e) => handleFieldChange("postcode", e.target.value, e)}
              maxLength={5}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder={_t("enterPostCode")}
            />
            {fieldErrors.postcode && (
              <p className="field-error-message">
                {getFieldErrorMessage("postcode")}
              </p>
            )}
          </div>
          <div className="forminput form-mobile-padding customInput row50">
            <label className="forminput label-text">{_t("stairway")}</label>
            <input
              type="text"
              className="form-control user-auth"
              name="staircase"
              value={formData?.staircase}
              onChange={handleInputChange}
              placeholder={_t("Enter Stairway")}
            />
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 ${fieldErrors.floor ? "field-error" : ""}`}
          >
            <label className="forminput label-text">
              {_t("floorNumber")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control user-auth ${fieldErrors.floor ? "is-invalid" : ""}`}
              name="floor"
              value={formData?.floor}
              onChange={(e) => handleFieldChange("floor", e.target.value, e)}
              placeholder={_t("enterFloorNumber")}
              onInput={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value !== "" && Number(value) > 40) {
                  value = "40";
                }
                e.target.value = value;
              }}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {fieldErrors.floor && (
              <p className="field-error-message">
                {getFieldErrorMessage("floor")}
              </p>
            )}
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 ${fieldErrors.door ? "field-error" : ""}`}
          >
            <label className="forminput label-text">
              {_t("letter")} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control user-auth ${fieldErrors.door ? "is-invalid" : ""}`}
              name="door"
              value={formData?.door}
              onChange={(e) => handleFieldChange("door", e.target.value, e)}
              placeholder={_t("enterLetter")}
            />
            {fieldErrors.door && (
              <p className="field-error-message">
                {getFieldErrorMessage("door")}
              </p>
            )}
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 relative ${fieldErrors.province ? "field-error" : ""}`}
            id="custom-select-search"
          >
            <label className="forminput label-text">
              {_t("province")} <span className="text-danger">*</span>
            </label>
            <Select
              className={`react-select-wrapper ${fieldErrors.province ? "is-invalid" : ""}`}
              classNamePrefix="react-select"
              name="province"
              value={
                formData?.province
                  ? { label: formData.province, value: formData.province }
                  : null
              }
              options={provincesData.map((p) => ({
                label: p.Provincia,
                value: p.Provincia,
              }))}
              placeholder={_t("selectProvince")}
              styles={compactSelectStyles()}
              onChange={(selected) => {
                handleFieldChange("province", selected?.value || "");
                handleFieldChange("municipality", "");
              }}
              isSearchable
            />
            <img
              src="/images/down-arrow-grey.svg"
              className="downArrow"
              alt="arrow"
            />
            {fieldErrors.province && (
              <p className="field-error-message">
                {getFieldErrorMessage("province")}
              </p>
            )}
          </div>
          <div
            className={`forminput form-mobile-padding customInput row50 relative ${!formData?.province ? "input-disabled" : ""} ${fieldErrors.municipality ? "field-error" : ""}`}
            id="custom-select-search"
          >
            <label className="forminput label-text">
              {_t("municipality")} <span className="text-danger">*</span>
            </label>
            <Select
              className={`react-select-wrapper ${fieldErrors.municipality ? "is-invalid" : ""}`}
              classNamePrefix="react-select"
              name="municipality"
              value={
                formData?.municipality
                  ? {
                      label: formData.municipality,
                      value: formData.municipality,
                    }
                  : null
              }
              options={
                formData?.province
                  ? provincesData
                      .find((p) => p.Provincia === formData.province)
                      ?.municipios.map((m) => ({ label: m, value: m })) || []
                  : []
              }
              placeholder={
                formData?.province
                  ? _t("selectMunicipality")
                  : _t("selectProvinceFirst")
              }
              onChange={(selected) =>
                handleFieldChange("municipality", selected?.value || "")
              }
              styles={compactSelectStyles()}
              isSearchable
              isDisabled={!formData?.province}
            />
            <img
              src="/images/down-arrow-grey.svg"
              className="downArrow"
              alt="arrow"
            />
            {fieldErrors.municipality && (
              <p className="field-error-message">
                {getFieldErrorMessage("municipality")}
              </p>
            )}
          </div>
          <div className="forminput form-mobile-padding customInput row50">
            <label className="forminput label-text">{_t("bedrooms")}</label>
            <input
              type="text"
              className="form-control user-auth"
              name="bedrooms"
              value={formData?.bedrooms}
              onChange={handleInputChange}
              placeholder={_t("enterBedrooms")}
              onInput={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value !== "" && Number(value) > 10) {
                  value = "10";
                }
                e.target.value = value;
              }}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="forminput form-mobile-padding customInput row50">
            <label className="forminput label-text">{_t("bathrooms")}</label>
            <input
              type="text"
              className="form-control user-auth"
              name="washrooms"
              value={formData?.washrooms}
              onChange={handleInputChange}
              placeholder={_t("enterBathrooms")}
              onInput={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value !== "" && Number(value) > 10) {
                  value = "10";
                }
                e.target.value = value;
              }}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Delete",
                ];
                if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          {isEdit && (
            <>
              <div
                className={`forminput form-mobile-padding customInput row50 ${fieldErrors.ad_url ? "field-error" : ""}`}
              >
                <label className="forminput label-text">{_t("adUrl")}</label>
                <input
                  type="url"
                  className={`form-control user-auth ${fieldErrors.ad_url ? "is-invalid" : ""}`}
                  name="ad_url"
                  value={formData?.ad_url ?? ""}
                  onChange={handleAdUrlChange}
                  placeholder={_t("enterAdUrl")}
                />
                {fieldErrors.ad_url && (
                  <p className="field-error-message">{_t("invalidUrl")}</p>
                )}
              </div>
              <div className="forminput form-mobile-padding customInput row50">
                <label className="forminput label-text">
                  {_t("calendarId")}
                </label>
                <input
                  type="text"
                  className="form-control user-auth"
                  name="calendar_id"
                  value={formData?.calendar_id ?? ""}
                  onChange={handleInputChange}
                  placeholder={_t("enterCalendarId")}
                />
              </div>
            </>
          )}
          <div className="forminput form-mobile-gap customInput row100">
            <label className="forminput label-text">
              {_t("propertyReferenceOptional")}
            </label>
            <input
              className="form-control user-auth"
              name="property_reference"
              value={formData?.property_reference}
              onChange={handleInputChange}
              placeholder={_t("enterPropertyReference")}
            />
          </div>
          <div className="btngroup row100 d-flex align-items-center justify-content-center custom-gap-3">
            <button
              className="commonButtonPropertyDark form-mobile-button-color commonButtonPropertyLight"
              type="button"
              onClick={() => (isEdit ? onClose() : setStep(1))}
            >
              {_t("goBack")}
            </button>
            <button
              disabled={submitLoading}
              className="btnNext commonButtonPropertyDark"
              type="submit"
            >
              {_t("submit")}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default FullAddressFormModal;
